import { dehydrate, hydrate } from '@tanstack/react-query';

/**
 * The phone app opens like a native one: on what it showed last time. What the
 * server sent (TanStack Query's cache) is kept on the device; on the next start it
 * is put back before the first screen draws, and every screen refreshes it in the
 * background (the data is older than staleTime). Only for the signed-in account it
 * was saved for: a different token, an old format or a week-old copy is dropped.
 */

const KEY = 'bb.cache';
const VERSION = 1;  // raise when what the screens expect from the API changes shape
const MAX_AGE = 7 * 24 * 3600 * 1000;
const MAX_CHARS = 3_500_000;  // localStorage keeps about 5 MB per app
// Never kept on the device: credentials, admin data and what is rarely opened anyway.
const SKIP = new Set(['admin', 'secrets', 'byok', 'mcp-tokens', 'webhooks', 'gmail', 'trash']);

/** A short tag of the token, so a cache is used only by the account that wrote it. */
export const tokenTag = (token) => {
  let h = 0;
  for (let i = 0; i < (token || '').length; i += 1) h = (h * 31 + token.charCodeAt(i)) | 0;
  return `${(token || '').length}:${h}`;
};

const keep = (query) => query.state.status === 'success' && query.state.data !== undefined
  && !query.queryKey.some((part) => typeof part === 'string' && SKIP.has(part));

/** What to store: the cache as JSON, the biggest queries left out until it fits. */
export const snapshot = (queryClient, token, now = Date.now()) => {
  const state = dehydrate(queryClient, { shouldDehydrateQuery: keep, shouldDehydrateMutation: () => false });
  const sized = state.queries.map((q) => [q, JSON.stringify(q).length]).sort((a, b) => a[1] - b[1]);
  let total = 0;
  const queries = [];
  sized.forEach(([q, n]) => {
    if (total + n <= MAX_CHARS) { queries.push(q); total += n; }
  });
  return JSON.stringify({ v: VERSION, tag: tokenTag(token), at: now, state: { ...state, queries } });
};

/** Puts the saved cache back. Returns whether there was one for this account. */
export const restoreCache = (queryClient, token, storage = window.localStorage, now = Date.now()) => {
  try {
    const raw = storage.getItem(KEY);
    if (!raw || !token) return false;
    const saved = JSON.parse(raw);
    if (saved.v !== VERSION || saved.tag !== tokenTag(token) || now - saved.at > MAX_AGE) {
      storage.removeItem(KEY);
      return false;
    }
    hydrate(queryClient, saved.state);
    return true;
  } catch {
    try { storage.removeItem(KEY); } catch { /* storage blocked */ }
    return false;
  }
};

export const clearCache = (storage = window.localStorage) => {
  try { storage.removeItem(KEY); } catch { /* storage blocked */ }
};

/**
 * Saves the cache a moment after the data changes (and when the app goes to the
 * background), as long as someone is signed in. Returns a function that stops it.
 */
export const persistCache = (queryClient, getToken, storage = window.localStorage) => {
  let timer = null;
  const save = () => {
    clearTimeout(timer);
    timer = null;
    const token = getToken();
    if (!token) return;
    try {
      storage.setItem(KEY, snapshot(queryClient, token));
    } catch {
      clearCache(storage);  // full: better nothing than a half-written copy
    }
  };
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.action?.type === 'success' && !timer) timer = setTimeout(save, 1500);
  });
  const onHide = () => { if (document.visibilityState === 'hidden') save(); };
  document.addEventListener('visibilitychange', onHide);
  return () => {
    unsubscribe();
    document.removeEventListener('visibilitychange', onHide);
    clearTimeout(timer);
  };
};
